// src/hooks/usePolling.js
// Re-runs a fetch/refresh callback on an interval so a page reflects new
// data (a new project, a new admin action, a new registration) without the
// user having to hit refresh -- the same pattern Installation.jsx already
// used successfully, pulled out here so other pages can reuse it instead
// of each hand-rolling their own setInterval/visibility bookkeeping.
//
// Automatically slows down (or pauses) while the browser tab is in the
// background, and refreshes immediately the moment it becomes visible
// again, so it doesn't burn network/battery on a tab nobody's looking at.
import { useEffect, useRef } from 'react';

export default function usePolling(callback, { intervalMs = 20000, hiddenIntervalMs = null, enabled = true } = {}) {
  // Stored in a ref so callers don't need to wrap their callback in
  // useCallback for this to pick up their latest closure every tick.
  const savedCallback = useRef(callback);
  useEffect(() => {
    savedCallback.current = callback;
  });

  useEffect(() => {
    if (!enabled) return undefined;
    let timer = null;

    const tick = () => savedCallback.current();
    const schedule = (ms) => {
      if (timer) clearInterval(timer);
      timer = setInterval(tick, ms);
    };

    schedule(intervalMs);

    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        schedule(intervalMs);
        tick(); // catch up immediately on returning to the tab
      } else if (hiddenIntervalMs) {
        schedule(hiddenIntervalMs);
      } else if (timer) {
        clearInterval(timer);
        timer = null;
      }
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      if (timer) clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [intervalMs, hiddenIntervalMs, enabled]);
}
